import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { ChatMessage, ContactInfo, TransMasterData, RecipientItem } from "../types";
import { prepareServiceoAppData, validateServiceoAppData } from "../utils/chatUtils";
import { toast } from "react-hot-toast";
import { getErrorMessage, getErrorStatus } from "@/lib/error-utils";
import { isAxiosError } from "axios";

export const useWebchatMessaging = (
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  contactInfo: ContactInfo,
  pollingControlRef: React.MutableRefObject<{ shouldStop: boolean }>,
  setHasSessionError: React.Dispatch<React.SetStateAction<boolean>>,
  transMasterData?: TransMasterData | null,
  selectedCandidate?: RecipientItem | null
) => {
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Polling function to check for AI response
  const pollForResponse = async (messageId: string): Promise<string> => {
    const maxAttempts = 100; // 100 attempts * 3 seconds = 5 minutes
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    while (attempts < maxAttempts) {
      // Check if polling should be stopped (user navigated away)
      if (pollingControlRef.current.shouldStop) {
        throw new Error("Polling stopped: User navigated to different feature");
      }

      try {
        const response = await customInstance({
          url: API_CONFIG.ENDPOINTS.WEBCHAT + `/response/${messageId}`,
          method: 'GET',
        });

        // Check if we got a valid response
        const responseData = response.data;
        let responseText = "";

        if (typeof responseData === 'string') {
          responseText = responseData.trim();
        } else if (responseData && typeof responseData === 'object') {
          const data = responseData as Record<string, unknown>;
          if (typeof data.response === 'string') {
            responseText = data.response;
          } else if (typeof data.message === 'string') {
            responseText = data.message;
          } else if (typeof data.text === 'string') {
            responseText = data.text;
          }
        }

        // If we got a valid response, return it
        if (responseText && responseText.trim()) {
          return responseText;
        }

        // If no response yet, wait and try again
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        // Check stop condition after wait
        if (pollingControlRef.current.shouldStop) {
          throw new Error("Polling stopped: User navigated to different feature");
        }

        attempts++;

      } catch (error) {
        console.error(`Polling error on attempt ${attempts + 1}:`, getErrorMessage(error));

        // If it's a 404 or similar, the message might not be ready yet
        const status = getErrorStatus(error);
        if (status === 404 || status === 202) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));

          // Check stop condition after wait
          if (pollingControlRef.current.shouldStop) {
            throw new Error("Polling stopped: User navigated to different feature");
          }

          attempts++;
          continue;
        }

        // For other errors, continue polling
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        // Check stop condition after wait
        if (pollingControlRef.current.shouldStop) {
          throw new Error("Polling stopped: User navigated to different feature");
        }

        attempts++;
      }
    }

    // If we've exhausted all attempts, throw a timeout error
    throw new Error("Response timeout: No response received within 5 minutes. Please check your connection and try again.");
  };

  const handleSendMessage = async (currentMessage: string, setCurrentMessage: React.Dispatch<React.SetStateAction<string>>, agentType?: string | null) => {
    if (!currentMessage.trim()) {
      toast.error(UI_MESSAGES.MESSAGE_REQUIRED);
      return;
    }

    // Generate UUID for this message
    const messageUuid = uuidv4();

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: currentMessage.trim(),
      isUser: true,
      timestamp: new Date(),
      uuid: messageUuid,
    };

    setMessages(prev => [...prev, userMessage]);

    const messageToSend = currentMessage.trim();
    setCurrentMessage("");
    setIsSendingMessage(true);

    // Reset polling control flag for new message only after any prior loop has exited
    pollingControlRef.current.shouldStop = true;
    // Yield to allow any in-flight poll iteration to observe the stop flag
    await new Promise(resolve => setTimeout(resolve, 0));
    pollingControlRef.current.shouldStop = false;

    try {
      // Prepare the base request data
      const requestData: any = {
        email: contactInfo.email || undefined,
        mobilePhoneNo: contactInfo.phone || undefined,
        message: messageToSend,
        agentType: agentType || undefined,
        subChannel: contactInfo.subChannel || undefined,
      };

      // Add jobId and workerId when ServiceoApp is selected
      if (contactInfo.subChannel === 'ServiceoApp') {
        const validation = validateServiceoAppData(transMasterData ?? null, selectedCandidate ?? null);
        
        if (!validation.isValid) {
          console.warn('ServiceoApp selected but missing required data:', validation.missingFields);
          toast.error(`Missing ServiceoApp data: ${validation.missingFields.join(', ')}`);
        }
        
        const serviceoAppData = prepareServiceoAppData(transMasterData ?? null, selectedCandidate ?? null);
        Object.assign(requestData, serviceoAppData);
      }

      // Send message to backend
      await customInstance({
        url: API_CONFIG.ENDPOINTS.WEBCHAT + '/message',
        method: 'POST',
        headers: {
          'X-Message-UUID': messageUuid,
        },
        data: requestData,
      });

      // Add a temporary "processing" message while polling
      const processingMessage: ChatMessage = {
        id: (Date.now() + 0.5).toString(),
        text: "Processing your message... Please wait.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, processingMessage]);

      // Start polling for response
      const aiResponse = await pollForResponse(messageUuid);

      // Remove the processing message
      setMessages(prev => prev.filter(msg => msg.id !== processingMessage.id));

      // Add AI response to chat
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, aiMessage]);

      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Webchat error:", getErrorMessage(error));

      // Extract error message from API response
      let errorMessage = "Sorry, I couldn't process your message. Please try again.";
      let toastMessage = "Failed to send message. Please try again.";

      const status = getErrorStatus(error);
      
      // Extract error data from response if available
      let errorData: { message?: string; error?: string; errors?: string[] } | string | undefined;
      if (isAxiosError(error)) {
        errorData = error.response?.data as { message?: string; error?: string; errors?: string[] } | string | undefined;
      }
      
      if (status) {
        // Handle HTTP status codes

        switch (status) {
          case 400:
            errorMessage = "Invalid request. Please check your message and try again.";
            break;
          case 401:
            errorMessage = "Your session has expired. Please refresh the page to continue chatting.";
            toastMessage = "Session expired. Please refresh the page to continue.";
            break;
          case 403:
            errorMessage = "Access denied. You don't have permission to send messages.";
            break;
          case 404:
            errorMessage = "Service not found. Please contact support.";
            break;
          case 429:
            errorMessage = "Too many requests. Please wait a moment and try again.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = `Server error (${status}). Please try again.`;
        }

        // Override with specific error message if available
        if (errorData && typeof errorData === 'object' && errorData.message) {
          errorMessage = errorData.message;
          toastMessage = errorData.message;
        } else if (errorData && typeof errorData === 'object' && errorData.error) {
          errorMessage = errorData.error;
          toastMessage = errorData.error;
        } else if (errorData && typeof errorData === 'object' && Array.isArray(errorData.errors)) {
          // Handle validation errors array
          errorMessage = errorData.errors.join(", ");
          toastMessage = errorData.errors.join(", ");
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
          toastMessage = errorData;
        } else {
          toastMessage = errorMessage;
        }
      } else if (error instanceof Error && error.message) {
        // Handle general error message (network errors, etc.)
        if (error.message.includes('Network Error')) {
          errorMessage = "Network error. Please check your connection and try again.";
          toastMessage = "Network error. Please check your connection.";
        } else if (error.message.includes('Session expired')) {
          errorMessage = "Your session has expired. Please refresh the page to continue chatting.";
          toastMessage = "Session expired. Please refresh the page to continue.";
        } else {
          errorMessage = error.message;
          toastMessage = error.message;
        }
      }

      toast.error(toastMessage);

      // Add error message to chat with refresh option for session expiration
      const isSessionExpired = (error instanceof Error && error.message?.includes('Session expired')) ||
        (isAxiosError(error) && error.response?.status === 401);

      if (isSessionExpired) {
        setHasSessionError(true);
      }

      const chatErrorMessage: ChatMessage = {
        id: (Date.now() + 2).toString(),
        text: isSessionExpired
          ? `❌ ${errorMessage}\n\n🔄 Click here to refresh the page and continue chatting.`
          : `❌ ${errorMessage}`,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, chatErrorMessage]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  return {
    isSendingMessage,
    handleSendMessage
  };
};